<template>
  <div class="trix_container">
    <slot name="toolbar" :toolbar-id="toolbarId">
      <toolbar :toolbar-id="toolbarId" :show-html-mode-toggle="showHtmlMode" :show-attach-files-button="showAttachFiles"
               :show-code-button="showCodeButton" :show-heading-button="showHeadingButton"
               @change-to-html-mode="changeToHtmlMode" />
    </slot>

    <trix-editor :id="editorId" ref="trix"
                 :contenteditable="true"
                 :class="['trix-content']"
                 :input="inputId"
                 placeholder=""
                 :toolbar="toolbarId"
                 :data-direct-upload-url="activeStorageUploadUrl"
                 :data-blob-url-template="activeStorageBlobUrlTemplate"
                 @trix-change="handleContentChange"
                 @trix-attachment-add="emitAttachmentAdd"
                 @trix-attachment-remove="emitAttachmentRemove"
                 @trix-file-accept="handleFileAccept"
                 @trix-focus="processTrixFocus"
                 @trix-blur="processTrixBlur"
                 @trix-action-invoke="emitActionInvoke" />

    <input :id="inputId"
           type="hidden"
           :name="inputName"
           :value="editorContent">
  </div>
</template>

<script>
import '@/action-text' // Initialize ActionText to handle attachments from the Trix editor
import kebabCase from 'lodash/kebabCase'
import Toolbar from './toolbar'

const BLOB_URL_TEMPLATE = '/rails/active_storage/blobs/redirect/:signed_id/:filename'

export default {
  components: { Toolbar },

  props: {
    allowedAttachmentTypes: {
      type: Array,
      required: false
    },

    inputName: {
      type: String,
      required: false,
      default: 'content'
    },

    showAttachFiles: {
      type: Boolean,
      required: false,
      default: true
    },

    showCodeButton: {
      type: Boolean,
      required: false,
      default: false
    },

    showHeadingButton: {
      type: Boolean,
      required: false,
      default: true
    },

    showHtmlMode: {
      type: Boolean,
      required: false,
      default: true
    },

    srcContent: {
      type: String,
      required: false,
      default: ''
    }
  },

  emits: ['blur', 'change-to-html-mode', 'focus', 'input', 'trix-attachment-add', 'trix-attachment-remove', 'trix-action-invoke'],

  data() {
    // Remove new lines from the content which are otherwise transformed to <br> tags
    const contentWithoutNewLines = this.srcContent ? this.srcContent.replace(/\n/g, '') : ''

    return {
      activeStorageBlobUrlTemplate: BLOB_URL_TEMPLATE,
      editorContent: contentWithoutNewLines,
      isActived: null
    }
  },

  computed: {
    activeStorageUploadUrl() {
      return this.agraConfiguration.activeStorageDirectUploadsPath
    },

    editorId() {
      return kebabCase(this.inputName)
    },

    inputId() {
      return `${kebabCase(this.inputName)}-input`
    },

    toolbarId() {
      return `${this.inputId}-toolbar`
    }
  },

  watch: {
    srcContent: {
      handler: 'handleInitialContentChange'
    }
  },

  methods: {
    attachmentIsAllowedType(file) {
      if (this.allowedAttachmentTypes) {
        return this.allowedAttachmentTypes.includes(file.type)
      } else {
        // If allowedAttachmentTypes has not been specified, then assume everything is allowed.
        return true
      }
    },

    changeToHtmlMode() {
      this.$emit('change-to-html-mode')
    },

    emitActionInvoke(evt) {
      // Re-emit the event for anything else that might want to handle it
      this.$emit('trix-action-invoke', evt)
    },

    emitAttachmentAdd(evt) {
      // Re-emit the event for anything else that might want to handle it
      this.$emit('trix-attachment-add', evt)
    },

    emitAttachmentRemove(evt) {
      // Re-emit the event for anything else that might want to handle it
      this.$emit('trix-attachment-remove', evt)
    },

    emitInput(newValue) {
      if (!this.uploadInProgress()) {
        this.$emit('input', newValue)
      }
    },

    focus() {
      this.isActived = true
      this.$refs.trix.focus()
    },

    getContentEndPosition() {
      return this.$refs.trix.editor.getDocument().toString().length - 1
    },

    handleContentChange(event) {
      this.editorContent = event.srcElement ? event.srcElement.value : event.target.value
      this.emitInput(this.editorContent)
    },

    handleFileAccept(event) {
      // If file attachments are not allowed, then prevent files from being attached
      // in case someone pastes or drag/drops a file into the editor.
      // This is the way recommended by official Trix docs here:
      // https://github.com/basecamp/trix?tab=readme-ov-file#storing-attached-files
      if (!this.showAttachFiles || !this.attachmentIsAllowedType(event.file)) {
        event.preventDefault()
      }
    },

    handleInitialContentChange(newContent) {
      newContent = newContent === undefined ? '' : newContent

      if (this.$refs.trix.editor && this.$refs.trix.editor.innerHTML !== newContent) {
        /* Update editor's content when initial content changed */
        this.editorContent = newContent

        /**
         *  If user are typing, then don't reload the editor,
         *  hence keep cursor's position after typing.
         */
        if (!this.isActived) {
          this.reloadEditorContent(this.editorContent)
        }
      }
    },

    insertAttachment(attachment) {
      this.$refs.trix.editor.insertAttachment(attachment)
    },

    processTrixBlur() {
      if (this.$refs.trix) {
        this.isActived = false
      }

      this.$emit('blur')
    },

    processTrixFocus() {
      if (this.$refs.trix) {
        this.isActived = true
      }

      this.$emit('focus')
    },

    reloadEditorContent(newContent) {
      // Reload HTML content
      this.$refs.trix.editor.loadHTML(newContent)

      // Move cursor to end of new content updated
      this.$refs.trix.editor.setSelectedRange(this.getContentEndPosition())
    },

    removeAttachment(attachment) {
      this.$refs.trix.editor.composition.removeAttachment(attachment)
    },

    uploadInProgress() {
      this.$refs.trix.editor.getDocument().getAttachments().map((attachment) => attachment.isPending()).length > 0
    }
  }
}
</script>

<style lang="scss" scoped>
@use "~mixins/rtl_only" as rtl_override;

.trix_container {
  max-width: 100%;
  height: auto;

  ::v-deep(.trix-button-group) {
    background-color: white;
  }

  ::v-deep(.trix-content) {
    background-color: white;

    @include rtl_override.rtl-only {
      li {
        margin-left: 0;
        margin-right: 1em;
      }
    }
  }
}
</style>
